import React, { ReactElement, useEffect, useState } from 'react';
import { IconRemove } from 'src/content/icons';
import { strings } from 'src/localization';
import css from './ServicesCard.module.scss';
import { ScrollBar } from '@framework-controls/core';
import { Services } from 'src/models';
import classNames from 'classnames';
import { ViewMode } from 'src/enums';

export interface IProps {
    services: Services[];
    removeService: (id: string, e: any) => void;
    totalMinCost: string;
    myCart: boolean;
    skipServiceReason?: string;
    viewMode?: ViewMode;
    theme: string;
    gmRedeemAmt: string | undefined;
    gmDealerCanEarn: string | undefined;
    gmDealerCanRedeem: string | undefined;
    modifyServices?: (e: any) => void;
    noServicesSelected?: (e: any) => void;
    currentMainPane: string;
    displayMCMessage?: boolean;
    highlightNoServicesSelected?: boolean;
    hidePriceLabel?: string;
}

const ServicesCard = (props: IProps) => {
    const noStartingAt = (props.theme === 'lexus' || props.theme === 'lexusMonogram') ? true : false;
    const [oneNoStartingAt, setOneNoStartingAt] = useState(false);
    let serviceKey = 'Summary';
    if (props.myCart) {
        serviceKey = 'MyCart';
    }
    // PMR 1462985 requested that we always underline the link
    const linkButtonClass = classNames(
        css.summaryEditLinkADA,
        {
            [css.summaryEditLinkBMW]: props.theme === 'bmw',
            [css.summaryEditLinkChevrolet]: props.theme === 'chevrolet',
            [css.summaryEditLinkIN]: props.theme === 'infiniti',
            [css.summaryEditLinkAcura]: props.theme === 'acura',
        },
    );

    const titleClasses = classNames(
        css.summaryTitle,
        {
            [css.marginTop]: (!props.myCart),
        },
        {
            [css.titleMyCart]: (props.myCart),
            [css.titleMyCartHeadingTextWeight]: props.myCart && (props.theme === 'bmw' || 'ford' || 'infiniti'),

        },
        {
            [css.summaryTitleHonda]: props.theme === 'honda',
            [css.summaryTitleBMW]: props.theme === 'bmw',
            [css.summaryTitleFord]: props.theme === 'ford',
            [css.summaryTitleIN]: props.theme === 'infiniti',
            [css.summaryTitleMazda]: props.theme === 'mazda',
            [css.summaryTitleAcura]: props.theme === 'acura',
            [css.summaryTitleGenesis]: props.theme === 'genesis',
        },
    );

    const servicesClasses = classNames(
        css.summaryCard,
        {
            [css.menuCard]: (props.myCart),
            [css.menuCardGMC]: (props.theme === 'gmc' && props.myCart),
            [css.menuCardMINI]: (props.theme === 'mini' && props.myCart),
            [css.summaryCardBMW]: (props.theme==='bmw' && !props.myCart),
            [css.summaryCardSPM]: (props.theme==='toyotaSPM'),
            [css.summaryCardLM]: (props.theme==='lexusMonogram'),
            [css.summaryCardCA]: (props.theme==='cadillac'),
            [css.summaryCardIN]: props.theme === 'infiniti',
        },
    );

    const servicesText = classNames(
        css.serviceText,
        {
            [css.serviceLexusCart]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
            [css.serviceLexusSummary]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && !props.myCart),
            [css.fontWeightBaseText]: props.theme === 'honda' || props.theme === 'mercedesBenz' || props.theme === 'bmw' ||
            props.theme === 'ford' || props.theme === 'acura',
            [css.serviceTextIN]: props.theme === 'infiniti',
            [css.serviceTextMazda]: props.theme === 'mazda',
            [css.serviceTextGenesis]: props.theme === 'genesis',
        },
    );

    const servicesPrice = classNames(
        css.servicePrice,
        {
            [css.serviceLexusCart]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
            [css.serviceLexusSummary]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && !props.myCart),
            [css.fontWeightBaseText]: props.theme === 'honda' || props.theme === 'mercedesBenz' || props.theme === 'bmw' ||
            props.theme === 'ford' || props.theme === 'acura',
            [css.servicePriceIN]: props.theme === 'infiniti',
            [css.servicePriceMazda]: props.theme === 'mazda',
            [css.servicePriceGenesis]: props.theme === 'genesis',
        },
    );

    const servicesIcon = classNames(
        css.icon,
        {
            [css.iconLexus]: ((props.theme === 'lexus' || props.theme === 'lexusMonogram') && props.myCart),
            [css.iconChevrolet]: ((props.theme === 'chevrolet')),
            [css.iconBuick]: (props.theme === 'buick' || props.theme === 'mercedesBenz'),
        },
    );

    const gmRewardsNewCost = classNames(
        css.gmRewardsNewCost,
        {
            [css.gmRewardsNewCostAu]: (props.theme === 'audi' && !props.myCart),
        },
    );

    const totalMinCostGM = classNames(
        css.totalMinCostGM,
        {
            [css.totalMinCostGM_Au]: (props.theme === 'audi' && !props.myCart),
        },
    );

    const totalMinCost = classNames(
        css.totalMinCost,
        {
            [css.totalMinCost_Au]: (props.theme === 'audi' && props.myCart),
            [css.totalMinCostSPM]: (props.theme === 'toyotaSPM'),
            [css.totalMinCostHonda]: props.theme === 'honda',
            [css.totalMinCostBMW]: props.theme === 'bmw',
            [css.totalMinCostFord]: props.theme === 'ford',
            [css.totalMinCostIN]: props.theme === 'infiniti',
            [css.totalMinCostMazda]: (props.theme === 'mazda'),
            [css.totalMinCostAcura]: (props.theme === 'acura'),
            [css.totalMinCostGenesis]: (props.theme === 'genesis'),
        },
    );

    let header = (<h4 id='servicesHeader' className={titleClasses}>{!props.myCart ? strings.Services : strings.MyCart}</h4>);

    if (props.viewMode && props.viewMode === ViewMode.EditAppt && props.modifyServices) {
        header = (
            <div id='servicesHeader' style={{ display: 'flex' }}>
                <h4 className={titleClasses}>{!props.myCart ? strings.Services : strings.MyCart}</h4>
                <div onClick={props.modifyServices} className={linkButtonClass}>{strings.UpdateServices}</div>
            </div>
        );
    }

    useEffect(() => {
        if (noStartingAt &&
            props.services?.find((op) => ((op.type === 6 || op.type === 1) && op.priceHigh === '' &&
            (op.priceLow !== '' && !op.priceLow?.includes('*'))))) {
            setOneNoStartingAt(true);

            return;
        } else {
            setOneNoStartingAt(false);
        }
    }, [noStartingAt, props.services]);

    if (props.skipServiceReason) {
        return (
            <div className={css.flexItemRight} id='skipServiceReason'>
                {header}
                <div className={servicesClasses + ' ' + css.flexBox}>
                    <p>{props.skipServiceReason}</p>
                </div>
            </div>
        );
    }

    const serviceNames = props.services.map((service) => {
        let serviceName = service.serviceName;
        if (props.theme === 'infiniti') {
            serviceName = serviceName.toLocaleLowerCase();
        }

        return serviceName;
    });

    let servicesList = props.services.map((service, index) => (
        <div key={index + serviceKey} className={css.service} id={'service' + index}>
            <h6 className={servicesText} id={'serviceNames' + index}>
                {serviceNames[index].length > 225 ? serviceNames[index]?.substring(0, 225) + '...' :
                    serviceNames[index]}
            </h6>
            <h6 className={servicesPrice}>{service.range ?? ''}</h6>
            <IconRemove
                id={'deleteBtn' + index}
                onClick={(e: any) => props.removeService(service.id, e)}
                className={servicesIcon}
            />
        </div>
    ));

    const selectServicesStyles = classNames(
        css.selectServices,
        {
            [css.selectServicesBMW]: props.theme === 'bmw',
            [css.selectServicesSubaru]: props.theme === 'subaru',
            [css.selectServicesAcura]: props.theme === 'acura',
        },
    );

    if (props.services.length === 0 && props.currentMainPane !== 'AddVehicle') {
        servicesList = [
            <div key={serviceKey} id='noServices'>
                <p key={serviceKey}
                    className={`${css.noServices} ${props.highlightNoServicesSelected ? css.noServicesHighlight : ''}`}>
                    {strings.NoServicesSelected}
                </p>
                <p onClick={props.noServicesSelected} className={selectServicesStyles}
                    id='noServicesClickHere'>
                    {strings.NoServicesClickHere}
                </p>
                <p className={`${css.noServices} ${props.highlightNoServicesSelected ? css.noServicesHighlight : ''}`}>
                    {strings.ToSelectAService}
                </p>
            </div>,
        ];
    }
    let totalCost: ReactElement | string = strings.CallDealerForPricing;
    let costAfterRewards: ReactElement | null = null;
    let estimatedRewards: ReactElement | null = null;

    if (props.hidePriceLabel === 'Y') {
        totalCost = strings.PricingConfirmedUponArrival;
    }

    if (props.totalMinCost !== '****') {
        totalCost = <div className={totalMinCost}>{strings.MinEstCost + Number(props.totalMinCost).toFixed(2)}</div>;
        if (props.gmRedeemAmt && parseFloat(props.gmRedeemAmt) > 0 &&
            props.gmDealerCanEarn === 'Y' &&
            props.gmDealerCanRedeem === 'Y') {
            estimatedRewards = (
                <div className={totalMinCostGM} id='estimatedRewards'>
                    <div className={css.gmLit}>{strings.EstimatedGMRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{parseFloat(props.gmRedeemAmt).toFixed(2).toString()}</div>
                </div>
            );
            totalCost = (
                <div className={totalMinCostGM} id='totalCost'>
                    <div className={css.gmLit}>{strings.MinEstCostGM}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{Number(props.totalMinCost).toFixed(2)}</div>
                </div>
            );

            // calculate the cost after gm rewards are applied.
            let newCost: number = Number(props.totalMinCost) - Number(props.gmRedeemAmt);
            costAfterRewards = (
                <div className={gmRewardsNewCost} id='costAfterRewards'>
                    <div className={css.gmLit}>{strings.MinCostAfterRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{newCost.toFixed(2).toString()}</div>
                </div>
            );
            if (newCost < 0) {
                newCost = 0;
                costAfterRewards = (
                    <div className={gmRewardsNewCost} id='zeroCostAfterRewards'>
                        <div className={css.gmLit}>{strings.MinCostAfterRewards}</div>
                        <div className={css.gmValue}>{strings.DollarGM}{newCost.toFixed(2).toString()}</div>
                    </div>
                );
            }
        }
    } else {
        if (props.gmRedeemAmt && parseFloat(props.gmRedeemAmt) > 0 &&
            props.gmDealerCanEarn === 'Y' &&
            props.gmDealerCanRedeem === 'Y') {
            estimatedRewards = (
                <div className={totalMinCost} id='estimatedRewardsOnly'>
                    <div className={css.gmLit}>{strings.EstimatedGMRewards}</div>
                    <div className={css.gmValue}>{strings.DollarGM}{parseFloat(props.gmRedeemAmt).toFixed(2).toString()}</div>
                </div>
            );
        }
    }

    let servicesArea: ReactElement | null = <div>{servicesList}</div>;
    if (serviceKey === 'MyCart') {
        servicesArea = <ScrollBar scrollVertical><div className={css.myCartServices}>{servicesList}</div></ScrollBar>;
    }

    const gmRewardsDisclaimer = (!props.myCart && props.gmDealerCanEarn === 'Y' && props.gmDealerCanRedeem === 'Y') ?
        <p>{strings.GMRewardsDisclaimerSummary}</p> : null;

    return (
        <div className={css.flexItemRight}>
            {header}
            <div className={servicesClasses} key={serviceKey}>
                {servicesArea}
            </div>
            <hr className={css.servicesHR}></hr>
            {totalCost}
            {estimatedRewards}
            {costAfterRewards}
            {gmRewardsDisclaimer}
            {props.displayMCMessage ? <div className={css.maintContractMessage} id='mcMessage'>
                {strings.MaintContractMessage + ' ' + strings.MaintContractSubheading3}
            </div>: null
            }
            <div className={css.detailsText} id='disclaimer'>
                {oneNoStartingAt ? strings.PricingDislaimerSPM : null}
            </div>
        </div>
    );
};

export default ServicesCard;
